import { useState } from "react";
import {Calculator, TrendingUp, DollarSign, Users, ChevronRight} from 'lucide-react';

const roles = [
  { title: "Haul Truck Operator", avgSalary: 92000, yearsExp: 0, trainingWeeks: 4 },
  { title: "Surface Drill Operator", avgSalary: 108000, yearsExp: 2, trainingWeeks: 8 },
  { title: "Underground Miner", avgSalary: 115000, yearsExp: 3, trainingWeeks: 12 },
  { title: "Processing Plant Op.", avgSalary: 98000, yearsExp: 1, trainingWeeks: 6 },
  { title: "Mine Electrician", avgSalary: 135000, yearsExp: 4, trainingWeeks: 0 },
  { title: "Safety Officer", avgSalary: 95000, yearsExp: 1, trainingWeeks: 4 },
];

export default function ROICalculator() {
  const [currentSalary, setCurrentSalary] = useState(60000);
  const [selectedRole, setSelectedRole] = useState(0);
  const [fifo, setFifo] = useState(true);

  const role = roles[selectedRole];
  const fifoBonusPct = fifo ? 0.12 : 0;
  const targetSalary = Math.round(role.avgSalary * (1 + fifoBonusPct));
  const annualGain = targetSalary - currentSalary;
  const fiveYearGain = annualGain * 5;
  const monthlyGain = Math.round(annualGain / 12);
  const roiPct = Math.round(((targetSalary - currentSalary) / currentSalary) * 100);

  const formatCurrency = (n: number) => {
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
    return `$${n}`;
  };

  return (
    <section className="py-20 px-4 sm:px-6" style={{ background: "linear-gradient(180deg, #111827 0%, #0f1623 100%)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Calculator size={16} style={{ color: "#f97316" }} />
            <span className="text-sm font-medium" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>// LEAD MAGNET</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Mining Career ROI Calculator
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Discover your earning potential in the mining industry. See how much more you could earn in 5 years.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Input Panel */}
          <div className="bento-card p-6 space-y-6">
            <h3 className="font-semibold text-white text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>Your Details</h3>

            {/* Current Salary */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm text-gray-400">Current Annual Salary</label>
                <span className="text-sm font-semibold" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{formatCurrency(currentSalary)}</span>
              </div>
              <input type="range" min={30000} max={150000} step={5000} value={currentSalary} onChange={(e) => setCurrentSalary(Number(e.target.value))} />
              <div className="flex justify-between text-xs text-gray-600 mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <span>$30K</span><span>$150K</span>
              </div>
            </div>

            {/* Target Role */}
            <div>
              <label className="text-sm text-gray-400 block mb-2">Target Mining Role</label>
              <div className="grid grid-cols-2 gap-2">
                {roles.map((r, i) => (
                  <button
                    key={r.title}
                    onClick={() => setSelectedRole(i)}
                    className="px-3 py-2 rounded-lg text-xs text-left transition-all"
                    style={
                      selectedRole === i
                        ? { background: "rgba(249,115,22,0.2)", borderColor: "rgba(249,115,22,0.5)", border: "1px solid", color: "#fb923c" }
                        : { background: "#2e3850", color: "#8896b0", border: "1px solid transparent" }
                    }
                  >
                    <span className="font-medium block">{r.title}</span>
                    <span style={{ fontFamily: "JetBrains Mono, monospace" }}>{formatCurrency(r.avgSalary)}/yr</span>
                  </button>
                ))}
              </div>
            </div>

            {/* FIFO Toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: "#2e3850" }}>
              <div>
                <p className="text-sm text-white font-medium">FIFO Roster</p>
                <p className="text-xs text-gray-500">+12% salary premium for fly-in fly-out</p>
              </div>
              <button
                onClick={() => setFifo(!fifo)}
                className="relative w-12 h-6 rounded-full transition-colors"
                style={{ background: fifo ? "#f97316" : "#3d4a63" }}
              >
                <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm" style={{ left: fifo ? "26px" : "2px" }}></span>
              </button>
            </div>

            {role.trainingWeeks > 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-xl text-sm" style={{ background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.15)" }}>
                <span style={{ color: "#f97316" }}>⏱</span>
                <span className="text-gray-300">~{role.trainingWeeks} weeks of training to qualify</span>
              </div>
            ) : null}
          </div>

          {/* Results Panel */}
          <div className="space-y-4">
            {/* Big ROI Number */}
            <div className="bento-card p-6 text-center orange-glow" style={{ borderColor: "rgba(249,115,22,0.25)", background: "linear-gradient(135deg, rgba(249,115,22,0.1), rgba(234,88,12,0.05))" }}>
              <p className="text-sm text-gray-400 mb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>5-YEAR EARNINGS GAIN</p>
              <p className="text-5xl font-black text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                {fiveYearGain >= 0 ? "+" : ""}{formatCurrency(fiveYearGain)}
              </p>
              <p className="text-sm" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>
                {roiPct >= 0 ? "+" : ""}{roiPct}% salary increase
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: DollarSign, label: "Annual Gain", value: formatCurrency(Math.abs(annualGain)), positive: annualGain >= 0 },
                { icon: TrendingUp, label: "Monthly Gain", value: formatCurrency(Math.abs(monthlyGain)), positive: monthlyGain >= 0 },
                { icon: Users, label: "Target Salary", value: formatCurrency(targetSalary), positive: true },
              ].map((stat) => (
                <div key={stat.label} className="bento-card p-4 text-center">
                  <stat.icon size={18} className="mx-auto mb-2" style={{ color: "#f97316" }} />
                  <p className="text-lg font-bold text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all hover:scale-105 hover:opacity-90" style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}>
              Find {role.title} Jobs Now <ChevronRight size={16} />
            </button>

            <p className="text-xs text-center text-gray-600">
              Based on current market rates. Salaries vary by location and employer.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
